import { useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import type { HandDetector, Hand } from "@tensorflow-models/hand-pose-detection";
import type { ObjectDetection, DetectedObject } from "@tensorflow-models/coco-ssd";

type WarningLevel = "critical" | "warning";

export type InterviewDetectionWarning =
  | {
      level: WarningLevel;
      title: string;
      message: string;
    }
  | null;

export type InterviewDetectionStatus = {
  hands: string;
  workspace: string;
};

type UseInterviewDetectionsArgs = {
  videoRef: RefObject<HTMLVideoElement>;
  canvasRef: RefObject<HTMLCanvasElement>;
  handDetector: HandDetector | null;
  objectDetector: ObjectDetection | null;
  isActive: boolean;
  flipHorizontal?: boolean; // whether to mirror overlay horizontally
  drawVideo?: boolean; // when true, draw the base video frame onto the canvas
  handDetectionIntervalMs?: number;
  workspaceDetectionIntervalMs?: number;
};

export type UseInterviewDetectionsResult = {
  warning: InterviewDetectionWarning;
  status: InterviewDetectionStatus;
  handsDetected: number;
  workspaceDetected: boolean;
  detectionRunning: boolean;
};

const HAND_INACTIVITY_MS = 45_000;
const WORKSPACE_INACTIVITY_MS = 5_000;

// Detection loop now self-paces based on device speed; no manual throttling intervals

const INITIAL_STATUS: InterviewDetectionStatus = {
  hands: "Initializing hand detection…",
  workspace: "Initializing workspace detection…",
};

/**
 * Orchestrates the hand + workspace detection pipeline for the mobile interview stream.
 * Handles drawing overlays, warning thresholds, and human readable status strings.
 */
const useInterviewDetections = ({
  videoRef,
  canvasRef,
  handDetector,
  objectDetector,
  isActive,
  flipHorizontal = true,
  drawVideo = false,
  handDetectionIntervalMs = 100,
  workspaceDetectionIntervalMs = 2000,
}: UseInterviewDetectionsArgs): UseInterviewDetectionsResult => {
  const [warning, setWarning] = useState<InterviewDetectionWarning>(null);
  const [status, setStatus] = useState<InterviewDetectionStatus>(INITIAL_STATUS);
  const [handsDetected, setHandsDetected] = useState(0);
  const [workspaceDetected, setWorkspaceDetected] = useState(false);
  const [detectionRunning, setDetectionRunning] = useState(false);

  const lastLeftHandTimeRef = useRef(Date.now());
  const lastRightHandTimeRef = useRef(Date.now());
  const lastWorkspaceTimeRef = useRef(Date.now());
  const lastHandDetectionTimeRef = useRef<number>(0);
  const lastWorkspaceDetectionTimeRef = useRef<number>(0);
  const latestHandsRef = useRef<Hand[]>([]);
  const latestWorkspaceObjectsRef = useRef<DetectedObject[]>([]);

  // Detection loop self-paces via configured intervals for hands/workspace

  useEffect(() => {
    if (!isActive) {
      setDetectionRunning(false);
      setWarning(null);
      setHandsDetected(0);
      setWorkspaceDetected(false);
      setStatus(INITIAL_STATUS);
      return;
    }

    lastHandDetectionTimeRef.current = 0;
    lastWorkspaceDetectionTimeRef.current = 0;
    latestHandsRef.current = [];
    latestWorkspaceObjectsRef.current = [];

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      return;
    }

    let ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    const syncCanvasSize = () => {
      const nextWidth = video.videoWidth;
      const nextHeight = video.videoHeight;
      if (!nextWidth || !nextHeight) {
        return;
      }
      if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
        canvas.width = nextWidth;
        canvas.height = nextHeight;
        const updatedContext = canvas.getContext("2d");
        if (!updatedContext) {
          return;
        }
        ctx = updatedContext;
      }
    };

    // Ensure canvas matches the video intrinsic resolution for accurate overlay coordinates
    syncCanvasSize();

    const detectorsReady = Boolean(handDetector && objectDetector);
    setDetectionRunning(detectorsReady);

    if (!detectorsReady) {
      setStatus({
        hands: "Preparing hand detection…",
        workspace: "Preparing workspace detection…",
      });
    }

    let animationFrameId: number;
    let stopLoop = false;
    // RAF id stored above; loop is self-paced via awaiting processFrame

    const drawHands = (predictions: Hand[], width: number) => {
      if (predictions.length === 0 || !ctx) {
        return;
      }

      const context = ctx;
      const jointColor = "#10B981";
      const palmColor = "#3B82F6";

      const connections = [
        [0, 1],
        [1, 2],
        [2, 3],
        [3, 4],
        [0, 5],
        [5, 6],
        [6, 7],
        [7, 8],
        [0, 9],
        [9, 10],
        [10, 11],
        [11, 12],
        [0, 13],
        [13, 14],
        [14, 15],
        [15, 16],
        [0, 17],
        [17, 18],
        [18, 19],
        [19, 20],
      ];

      predictions.forEach((hand) => {
        const keypoints = hand.keypoints;

        context.beginPath();
        context.strokeStyle = palmColor;
        context.lineWidth = 4;
        connections.forEach(([start, end]) => {
          const startPoint = keypoints[start];
          const endPoint = keypoints[end];

          const xStart = flipHorizontal ? width - startPoint.x : startPoint.x;
          const xEnd = flipHorizontal ? width - endPoint.x : endPoint.x;

          context.moveTo(xStart, startPoint.y);
          context.lineTo(xEnd, endPoint.y);
        });
        context.stroke();

        keypoints.forEach((keypoint, index) => {
          const x = flipHorizontal ? width - keypoint.x : keypoint.x;
          const y = keypoint.y;

          context.beginPath();
          context.arc(x, y, index === 0 ? 8 : 4, 0, 2 * Math.PI);
          context.fillStyle = index === 0 ? palmColor : jointColor;
          context.fill();
        });
      });
    };

    const drawObjects = (predictions: DetectedObject[], width: number) => {
      if (!ctx) return;
      
      const context = ctx;
      
      predictions.forEach((prediction) => {
        const [x, y, bboxWidth, bboxHeight] = prediction.bbox;
        const drawX = flipHorizontal ? width - x - bboxWidth : x;

        context.strokeStyle = "#FBBF24";
        context.lineWidth = 3;
        context.strokeRect(drawX, y, bboxWidth, bboxHeight);

        context.fillStyle = "#FBBF24";
        const label = `${prediction.class} (${Math.round(prediction.score * 100)}%)`;
        context.font = "16px Inter, sans-serif";
        const textWidth = context.measureText(label).width;
        context.fillRect(drawX, y - 22, textWidth + 8, 22);

        context.fillStyle = "#111827";
        context.fillText(label, drawX + 4, y - 6);
      });
    };

    const updateWarningState = (hands: Hand[], workspaceActive: boolean, currentTime: number) => {
      const handsDetectionStale =
        currentTime - lastHandDetectionTimeRef.current > handDetectionIntervalMs * 1.5;
      const workspaceDetectionStale =
        currentTime - lastWorkspaceDetectionTimeRef.current > workspaceDetectionIntervalMs * 1.5;

      const leftHandMissingDuration = currentTime - lastLeftHandTimeRef.current;
      const rightHandMissingDuration = currentTime - lastRightHandTimeRef.current;
      const workspaceMissingDuration = currentTime - lastWorkspaceTimeRef.current;

      let nextWarning: InterviewDetectionWarning = null;

      if (!handsDetectionStale) {
        const handCritical =
          leftHandMissingDuration > HAND_INACTIVITY_MS || rightHandMissingDuration > HAND_INACTIVITY_MS;
        if (handCritical) {
          const missingBoth =
            leftHandMissingDuration > HAND_INACTIVITY_MS && rightHandMissingDuration > HAND_INACTIVITY_MS;
          const message = missingBoth
            ? "Both hands are out of view. This is a critical integrity risk."
            : leftHandMissingDuration > HAND_INACTIVITY_MS
            ? `Left hand missing for ${Math.ceil(leftHandMissingDuration / 1000)}s. Keep both hands visible.`
            : `Right hand missing for ${Math.ceil(rightHandMissingDuration / 1000)}s. Keep both hands visible.`;

          nextWarning = {
            level: "critical",
            title: "🚨 Critical Violation",
            message,
          };
        }
      }

      if (!workspaceDetectionStale && !workspaceActive && workspaceMissingDuration > WORKSPACE_INACTIVITY_MS) {
        nextWarning = {
          level: "warning",
          title: "⚠️ Suspicious Activity",
          message: `Laptop screen/keyboard not detected for ${Math.ceil(
            workspaceMissingDuration / 1000,
          )}s. Keep your workspace clearly visible.`,
        };
      }

      setWarning(nextWarning);

      setHandsDetected(hands.length);
      setWorkspaceDetected(workspaceActive);

      const countdownSeconds = Math.max(
        0,
        HAND_INACTIVITY_MS - Math.max(leftHandMissingDuration, rightHandMissingDuration),
      );

      let handsStatus: string;
      if (handsDetectionStale) {
        handsStatus = "⏳ Awaiting next hand check…";
      } else if (hands.length > 0) {
        handsStatus = `✅ Hands visible (${hands.length}).`;
      } else {
        handsStatus = `⚠️ No hands detected. Critical warning in ${Math.ceil(countdownSeconds / 1000)}s.`;
      }

      let workspaceStatus: string;
      if (workspaceDetectionStale) {
        workspaceStatus = "⏳ Awaiting next workspace check…";
      } else if (workspaceActive) {
        workspaceStatus = "✅ Laptop workstation detected.";
      } else {
        workspaceStatus = `⌨️ Laptop/keyboard visibility risk${workspaceMissingDuration > 0 ? ` (${Math.ceil(
          workspaceMissingDuration / 1000,
        )}s)` : ""}.`;
      }

      setStatus({
        hands: handsStatus,
        workspace: workspaceStatus,
      });
    };

    const processFrame = async () => {
      const now = Date.now();

      // Keep canvas resolution in sync with any dynamic stream resolution changes (WebRTC renegotiation)
      syncCanvasSize();

      if (!video.videoWidth || !video.videoHeight) {
        // Video dimensions are unavailable (stream renegotiating); clear stale drawings and skip.
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }

      if (canvas.width === 0 || canvas.height === 0) {
        return;
      }

      if (!ctx) return;

      // Transparent overlay only; do not redraw the video element
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Optionally draw the base video into the canvas (for canvas-only rendering)
      if (drawVideo) {
        ctx.save();
        if (flipHorizontal) {
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        ctx.restore();
      }

      if (!detectorsReady || !handDetector || !objectDetector) {
        latestHandsRef.current = [];
        latestWorkspaceObjectsRef.current = [];
        return;
      }

      const handDue = now - lastHandDetectionTimeRef.current >= handDetectionIntervalMs;
      const workspaceDue = now - lastWorkspaceDetectionTimeRef.current >= workspaceDetectionIntervalMs;
      const detectionTasks: Array<Promise<void>> = [];

      if (handDue) {
        detectionTasks.push(
          handDetector
            .estimateHands(video, { flipHorizontal: false })
            .then((handPredictions) => {
              const detectionTimestamp = Date.now();
              latestHandsRef.current = handPredictions;
              lastHandDetectionTimeRef.current = detectionTimestamp;
              handPredictions.forEach((handPrediction) => {
                if (handPrediction.handedness === "Left") {
                  lastLeftHandTimeRef.current = detectionTimestamp;
                } else if (handPrediction.handedness === "Right") {
                  lastRightHandTimeRef.current = detectionTimestamp;
                }
              });
            })
            .catch(() => {
              latestHandsRef.current = [];
              lastHandDetectionTimeRef.current = Date.now();
            }),
        );
      }

      if (workspaceDue) {
        detectionTasks.push(
          objectDetector
            .detect(video)
            .then((rawObjects) => {
              const filtered = rawObjects.filter(
                (prediction) =>
                  (prediction.class === "laptop" || prediction.class === "keyboard") && prediction.score > 0.6,
              );
              const detectionTimestamp = Date.now();
              latestWorkspaceObjectsRef.current = filtered;
              lastWorkspaceDetectionTimeRef.current = detectionTimestamp;
              if (filtered.length > 0) {
                lastWorkspaceTimeRef.current = detectionTimestamp;
              }
            })
            .catch(() => {
              latestWorkspaceObjectsRef.current = [];
              lastWorkspaceDetectionTimeRef.current = Date.now();
            }),
        );
      }

      if (detectionTasks.length) {
        await Promise.all(detectionTasks);
      }

      const hands = latestHandsRef.current;
      const objects = latestWorkspaceObjectsRef.current;
      const workspaceSeen = objects.length > 0;

      // Draw overlays based on latest detections (even if they did not update this frame)
      drawHands(hands, canvas.width);
      drawObjects(objects, canvas.width);

      const evaluationTime = Date.now();
      updateWarningState(hands, workspaceSeen, evaluationTime);
    };

    const detectionLoop = async () => {
      if (stopLoop) {
        return;
      }
      await processFrame();
      animationFrameId = window.requestAnimationFrame(detectionLoop);
    };

    lastLeftHandTimeRef.current = Date.now();
    lastRightHandTimeRef.current = Date.now();
    lastWorkspaceTimeRef.current = Date.now();
    setDetectionRunning(true);
    detectionLoop();

    return () => {
      stopLoop = true;
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      setDetectionRunning(false);
      setWarning(null);
      setHandsDetected(0);
      setWorkspaceDetected(false);
      setStatus(INITIAL_STATUS);
    };
  }, [
    isActive,
    handDetector,
    objectDetector,
    videoRef,
    canvasRef,
    flipHorizontal,
    drawVideo,
    handDetectionIntervalMs,
    workspaceDetectionIntervalMs,
  ]);

  return useMemo(
    () => ({
      warning,
      status,
      handsDetected,
      workspaceDetected,
      detectionRunning,
    }),
    [warning, status, handsDetected, workspaceDetected, detectionRunning],
  );
};

export default useInterviewDetections;
